package org.matthewo

import emotion.react.css
import js.objects.recordOf
import org.matthewo.components.FitText
import org.matthewo.components.SVG
import org.matthewo.components.YearSelector
import org.matthewo.config.CONFIG
import org.matthewo.schema.ALL_YEARS
import org.matthewo.styles.TITLE_FONT_COLOR
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.nav
import tanstack.react.router.Link
import tanstack.react.router.useLocation
import tanstack.router.core.RoutePath
import web.cssom.*
import kotlin.experimental.ExperimentalTypeInference

sealed class HeaderContent
data class DefaultHeader(val years: List<Int>? = null) : HeaderContent()
data class TitleHeader(val text: String) : HeaderContent()
data class ElementHeader(val center: Boolean = true, val builder: ChildrenBuilder.() -> Unit) : HeaderContent()

val HeaderContext = createContext<StateInstance<HeaderContent>>()

@OptIn(ExperimentalTypeInference::class)
@OverloadResolutionByLambdaReturnType
fun useHeader(vararg dependencies: Any?, value: () -> HeaderContent) {
    var header by use(HeaderContext)!!
    useEffect(*dependencies) {
        header = value()
    }
}

fun useDefaultHeader(years: List<Int>? = null) = useHeader { DefaultHeader(years) }

fun useTitleHeader(vararg dependencies: Any?, value: () -> String) = useHeader(*dependencies) { TitleHeader(value()) }

fun useElementHeader(center: Boolean = true, vararg dependencies: Any?, value: ChildrenBuilder.() -> Unit) = useHeader(*dependencies) { ElementHeader(center, value) }

enum class HeaderLink(val link: RoutePath) {
    SEARCH(SEARCH_PATH),
    TABLE(TABLE_PATH),
    GRAPH(GRAPH_PATH);

    val displayName = name.first().uppercase() + name.drop(1).lowercase()

    companion object {
        fun fromPath(path: String): HeaderLink? {
            val yearLocation = path
                .split("/")
                .indexOfFirst { it.toIntOrNull() in ALL_YEARS }
            val yearAgnosticPath =
                if (-1 == yearLocation) path.drop(1)
                else path.split("/", limit = yearLocation + 2).lastOrNull() ?: return null
            entries.forEach { header ->
                if (header.link.toString().endsWith(yearAgnosticPath)) return header
            }
            return null
        }
    }
}

fun String.extractYear() =
    split("/").mapNotNull { it.toIntOrNull() }.firstOrNull { it in ALL_YEARS }

val Header = FC("Header") {
    val location = useLocation()
    val pageHeader by use(HeaderContext)!!
    var showMenu by useState(false)
    useEffect(pageHeader) {
        showMenu = false
    }

    nav {
        css {
            width = 100.vw - 1.rem
            height = 1.2.em
            minHeight = 1.2.em
            maxHeight = 1.2.em
            margin = 0.5.rem
            display = Display.inlineFlex
            padding = 0.px
            fontSize = 3.rem
            userSelect = None.none
            whiteSpace = WhiteSpace.nowrap
        }

        val currentHeader = HeaderLink.fromPath(location.pathname)

        FitText {
            css {
                width = 100.pct
            }

            div {
                val pageHeader = pageHeader
                css {
                    width = 100.pct
                    margin = 0.0.px
                    display = Display.flex
                    flexDirection = FlexDirection.row
                    gap = 0.75.em
                    justifyContent =
                        if (pageHeader !is DefaultHeader || null != pageHeader.years) JustifyContent.spaceBetween else JustifyContent.spaceEvenly
                    padding = 0.0.px
                }

                if (pageHeader !is DefaultHeader) {
                    SVG {
                        css {
                            color = TITLE_FONT_COLOR
                            width = 1.2.em
                            height = 1.2.em
                        }
                        onClick = {
                            showMenu = !showMenu
                        }
                        url = if (showMenu) "/close.svg" else "/hamburger.svg"
                    }
                }

                when {
                    pageHeader is DefaultHeader || showMenu -> {
                        val years = (pageHeader as? DefaultHeader)?.years
                        if (null != years) {
                            div {}
                        }
                        HeaderLink.entries.forEach { header ->
                            Link {
                                key = Key(header.name)
                                css {
                                    color = if (header == currentHeader) {
                                        NamedColor.white
                                    } else {
                                        TITLE_FONT_COLOR
                                    }
                                }

                                to = header.link
                                params = recordOf(
                                    YEAR_PARAM to (location.pathname.extractYear()?.toString() ?: CONFIG.CURRENT_YEAR.toString())
                                )

                                +header.displayName
                            }
                        }
                        if (null != years) {
                            YearSelector {
                                this.years = years
                            }
                        }
                    }

                    pageHeader is TitleHeader ->
                        div {
                            css {
                                color = TITLE_FONT_COLOR
                                textAlign = TextAlign.center
                                whiteSpace = WhiteSpace.normal
                            }
                            +pageHeader.text
                        }

                    pageHeader is ElementHeader -> pageHeader.builder(this)
                }

                if (showMenu || pageHeader !is DefaultHeader && (pageHeader !is ElementHeader || pageHeader.center)) {
                    div {
                        css {
                            width = 1.2.em
                            height = 1.2.em
                            flexShrink = number(1.0)
                        }
                    }
                }
            }
        }
    }
}
